/**
 * GenericFormDialog - Reusable form dialog with react-hook-form integration
 *
 * This component reduces form dialog duplication by providing:
 * - Integrated react-hook-form management
 * - Zod schema validation
 * - Accessible dialog with skip links and focus management
 * - Consistent submit/cancel handling
 * - Type-safe render prop pattern for form fields
 *
 * Usage Example:
 * ```tsx
 * <GenericFormDialog
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Add User"
 *   submitLabel="Create User"
 *   resolver={zodResolver(userCreateSchema)}
 *   defaultValues={{ email: '', full_name: '' }}
 *   onSubmit={handleSubmit}
 *   isSubmitting={mutation.isPending}
 * >
 *   {(form) => (
 *     <VStack gap={4}>
 *       <FormField label="Email" isInvalid={!!form.formState.errors.email}>
 *         <Input {...form.register("email")} type="email" />
 *       </FormField>
 *     </VStack>
 *   )}
 * </GenericFormDialog>
 * ```
 */

import {
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Portal,
  Text,
} from "@chakra-ui/react"
import { memo, useCallback, useEffect, useMemo } from "react"
import {
  type FieldValues,
  type Resolver,
  type UseFormReturn,
  useForm,
} from "react-hook-form"

export interface GenericFormDialogProps<TFormData extends FieldValues> {
  // Dialog state
  isOpen: boolean
  onClose: () => void

  // Dialog configuration
  title: string
  submitLabel?: string
  cancelLabel?: string
  size?: "sm" | "md" | "lg" | "xl" | "full"

  // Form configuration
  resolver: Resolver<TFormData>
  defaultValues: TFormData
  mode?: "onBlur" | "onChange" | "onSubmit" | "onTouched" | "all"

  // Form rendering (render prop pattern)
  children: (form: UseFormReturn<TFormData>) => React.ReactNode

  // Submission
  onSubmit: (data: TFormData) => Promise<void> | void
  isSubmitting?: boolean
  loadingText?: string

  // Optional accessibility features
  descriptionHelper?: string
  showSkipLink?: boolean
  finalFocusRef?: React.RefObject<HTMLElement | null>
}

function GenericFormDialogInner<TFormData extends FieldValues>({
  isOpen,
  onClose,
  title,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  size = "md",
  resolver,
  defaultValues,
  mode = "onBlur",
  children,
  onSubmit,
  isSubmitting = false,
  loadingText = "Submitting...",
  descriptionHelper,
  showSkipLink = false,
  finalFocusRef,
}: GenericFormDialogProps<TFormData>) {
  // Normalize default values (ensure all fields exist)
  const normalizedDefaults = useMemo(() => {
    const normalized: Record<string, unknown> = { ...defaultValues }
    Object.keys(normalized).forEach((key) => {
      if (normalized[key] === null || normalized[key] === undefined) {
        normalized[key] = ""
      }
    })
    return normalized as TFormData
  }, [defaultValues])

  // Initialize form
  const form = useForm<TFormData>({
    resolver,
    mode,
    defaultValues: normalizedDefaults as never,
  })

  // Reset form when dialog opens with new default values
  useEffect(() => {
    if (isOpen) {
      form.reset(normalizedDefaults as never)
    }
  }, [isOpen, normalizedDefaults, form])

  // Handle form submission
  const handleSubmit = useCallback(
    async (data: TFormData) => {
      try {
        await onSubmit(data)
        // Only close and reset on success
        if (!isSubmitting) {
          onClose()
          form.reset()
        }
      } catch (error) {
        // Error handling is done by the parent's mutation
        console.error("Form submission error:", error)
      }
    },
    [onSubmit, onClose, form, isSubmitting],
  )

  // Handle dialog close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose()
      form.reset(normalizedDefaults as never)
    }
  }, [onClose, form, isSubmitting, normalizedDefaults])

  // Handle Escape key
  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (!details.open && !isSubmitting) {
        handleClose()
      }
    },
    [handleClose, isSubmitting],
  )

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={handleOpenChange}
      size={size}
      finalFocusEl={() => finalFocusRef?.current || null}
    >
      <Portal>
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent
            as="form"
            onSubmit={form.handleSubmit(handleSubmit as never)}
            aria-label={title}
          >
            {/* Skip link for accessibility */}
            {showSkipLink && (
              <a
                href="#dialog-actions"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  zIndex: 9999,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.left = "10px"
                  e.currentTarget.style.top = "10px"
                }}
                onBlur={(e) => {
                  e.currentTarget.style.left = "-9999px"
                }}
              >
                Skip to dialog actions
              </a>
            )}

            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogCloseTrigger disabled={isSubmitting} />
            </DialogHeader>

            <DialogBody>
              {descriptionHelper && (
                <Text fontSize="sm" color="gray.600" mb={4}>
                  {descriptionHelper}
                </Text>
              )}

              {/* Render form fields via render prop */}
              {children(form as unknown as UseFormReturn<TFormData>)}
            </DialogBody>

            <DialogFooter id="dialog-actions">
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={isSubmitting}
                type="button"
              >
                {cancelLabel}
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                loading={isSubmitting}
                loadingText={loadingText}
                disabled={isSubmitting || !form.formState.isValid}
              >
                {submitLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  )
}

/**
 * GenericFormDialog with memo for performance
 * Note: We use a wrapper function to enable generic types with memo
 */
export const GenericFormDialog = memo(
  GenericFormDialogInner,
) as typeof GenericFormDialogInner

// Add displayName to the inner component for debugging
GenericFormDialogInner.displayName = "GenericFormDialog"

export default GenericFormDialog
